import LibraryElementEnum from 'client/enums/LibraryElementEnum'
import i18n from 'client/i18n'
import {
  getByType,
  getChildrenEntities,
  getEntityById,
} from 'client/reducers/pageReducer'
import { getFirstEntityByType } from 'client/store/page/pageSelectors'
import { getProduct } from 'client/store/payment/paymentSelectors'
import { slugs } from 'common/constants/inputTypes'
import { pageTypes } from 'common/constants/pageTypes'
import { paymentProcessorsRequiredFields } from 'common/constants/paymentProcessorsRequiredFields'
import { buttonActionsTypes } from 'common/constants/settings'
import structureTypes from 'common/constants/structureTypes'
import toolBoxTypes from 'common/constants/toolBoxTypes'
import { ButtonActionTypeEnum } from 'common/enums/ButtonActionTypeEnum'
import { DigitalProductResourcesTypeEnum } from 'common/types/entities/ProductInterface'
import * as entityUtils from 'common/utils/entityUtils'
import { checker, validator } from 'common/utils/validator'
import EntityTypeEnum from '../../common/enums/entityTypeEnum'
import { PageTypeEnum } from '../../common/enums/PageTypeEnum'
import { PageValidationErrorNew } from '../../common/errors/PageValidationErrorNew'

const toolBoxTypesKeys = Object.keys(toolBoxTypes).concat(['BLOCK'])
const structureTypesKeys = Object.keys(structureTypes)
const typedEntityTypes = Object.values(EntityTypeEnum)

const PAGE_SIZE_BYTES_LIMIT = 4_718_592 // 4.5Mb Nginx has 5mb limit

const filterSectionLayout = type =>
  toolBoxTypes.TOOLBOX_SECTION !== type &&
  type !== LibraryElementEnum.LibrarySection

const filterBlogContent = type =>
  [
    toolBoxTypes.TOOLBOX_BLOG_CONTENT_PLACEHOLDER,
    LibraryElementEnum.LibraryBlogContentPlaceholder,
    toolBoxTypes.TOOLBOX_BLOG_POST_BODY,
    LibraryElementEnum.LibraryBlogPostBody,
    structureTypes.BLOG_CONTENT_PLACEHOLDER,
    EntityTypeEnum.BlogContentPlaceholder,
    structureTypes.BLOG_POST_CONTENT_PLACEHOLDER,
    EntityTypeEnum.BlogPostContentPlaceholder,
    structureTypes.BLOG_POST_BODY,
    EntityTypeEnum.BlogPostBody,
  ].includes(type) === false

const containerAllowedTypes = toolBoxTypesKeys
  .filter(filterSectionLayout)
  .concat(structureTypesKeys)
  .concat(typedEntityTypes)
  .filter(t => t !== structureTypes.SECTION && t !== EntityTypeEnum.Section)

export const getAllowedTypes = type => {
  switch (type) {
    case structureTypes.BODY:
    case EntityTypeEnum.Body:
    case structureTypes.BLOG_POST_BODY:
    case EntityTypeEnum.BlogPostBody:
    case structureTypes.BLOG_PAGE_BODY:
    case EntityTypeEnum.BlogPageBody:
    case structureTypes.BLOG_POST_LAYOUT_BODY:
    case EntityTypeEnum.BlogPostLayoutBody:
      return toolBoxTypesKeys
        .concat(structureTypesKeys)
        .concat(typedEntityTypes)
    case structureTypes.COLUMN:
    case EntityTypeEnum.Column:
    case structureTypes.CONTENT_BOX:
    case EntityTypeEnum.ContentBox:
    case EntityTypeEnum.InlineBody:
    case EntityTypeEnum.Popup:
    case structureTypes.SECTION:
    case EntityTypeEnum.Section:
    case EntityTypeEnum.Survey:
    case EntityTypeEnum.TwoStepPaymentFormStepPayment:
    case EntityTypeEnum.TwoStepPaymentFormStepOptIn:
    case EntityTypeEnum.ContactUs:
    case EntityTypeEnum.Carousel:
      return containerAllowedTypes
    case EntityTypeEnum.CarouselItem: {
      return containerAllowedTypes
        .filter(entityType => EntityTypeEnum.Carousel !== entityType)
        .filter(filterBlogContent)
    }
    case EntityTypeEnum.FaqItem:
      return containerAllowedTypes.filter(filterBlogContent)
    case EntityTypeEnum.WebsitePageBody:
      return containerAllowedTypes
    case EntityTypeEnum.WebsiteFooter:
      return containerAllowedTypes
    default:
      return []
  }
}

const flatArray = (acc, val) => acc.concat(val)

const checkContentLengthNotExceeded = pageState => {
  return JSON.stringify(pageState).length < PAGE_SIZE_BYTES_LIMIT
}

const checkHasNotMultiplePaymentMethods = state => {
  const paymentMethodsOld = getByType(state, structureTypes.PAYMENT_METHOD)
  const paymentMethods = getByType(state, EntityTypeEnum.PaymentMethod)
  return paymentMethodsOld.concat(paymentMethods).length <= 1
}

const checkHasPaymentButton = state => {
  const bookingPaidCalendar = getByType(
    state,
    EntityTypeEnum.BookingPaidCalendar,
  )
  if (bookingPaidCalendar.length) {
    const paymentButton = getByType(state, EntityTypeEnum.PaymentButton)
    const paymentButtonOld = getByType(state, structureTypes.PAYMENT_BUTTON)

    if (!!paymentButton.length || paymentButtonOld.length) {
      return false
    }
  }

  return true
}

const checkHasNotMultiplePaidCalendars = state => {
  const bookingPaidCalendar = getByType(
    state,
    EntityTypeEnum.BookingPaidCalendar,
  )
  return bookingPaidCalendar.length <= 1
}

const checkHasPaidCalendarWhenStateHasBooking = (state, paymentState) => {
  const bookingEvent = paymentState?.digitalProduct?.resources?.find(
    ({ type }) => type === DigitalProductResourcesTypeEnum.BookingEvent,
  )
  if (bookingEvent) {
    const bookingPaidCalendar = getByType(
      state,
      EntityTypeEnum.BookingPaidCalendar,
    )
    return !!bookingPaidCalendar.length
  }

  return true
}

const checkHasProductElementWhenStateHasProduct = (pageState, paymentState) => {
  if (
    pageState.type === PageTypeEnum.OfferPage &&
    paymentState &&
    getProduct(paymentState) &&
    !getFirstEntityByType(pageState, EntityTypeEnum.Product)
  ) {
    return false
  }

  return true
}

const checkHasNotMultipleCalendars = state => {
  const calendarEntities = getByType(state, EntityTypeEnum.BookingCalendar)
  return calendarEntities.length <= 1
}

const checkCalendarEventSelected = state => {
  const calendarEntities = getByType(state, EntityTypeEnum.BookingCalendar)
  if (!calendarEntities.length) return true

  const calendarEntity = calendarEntities.find(
    entity => entity.type === EntityTypeEnum.BookingCalendar,
  )
  return !!calendarEntity.eventId
}

const checkHasPaymentButtonInOfferPage = state => {
  if (state.type === PageTypeEnum.OfferPage) {
    const paymentButton = getByType(state, EntityTypeEnum.PaymentButton)
    const paidCalendar = getByType(state, EntityTypeEnum.BookingPaidCalendar)
    const oldPaymentButton = getByType(state, structureTypes.PAYMENT_BUTTON)
    const twoStepForm = getByType(state, EntityTypeEnum.TwoStepPaymentForm)
    return !!(
      paymentButton.length ||
      twoStepForm.length ||
      oldPaymentButton.length ||
      paidCalendar.length
    )
  }
  return true
}

const checkHasSearchResultsInSearchPage = state => {
  if (state.type === PageTypeEnum.BlogSearch) {
    const searchResults = getByType(state, EntityTypeEnum.SearchResults)
    return !!searchResults.length
  }
  return true
}

const checkBlogPostContentOnePlaceholder = state => {
  if (state.type === pageTypes.blogPostLayout) {
    const entities = getByType(
      state,
      structureTypes.BLOG_POST_CONTENT_PLACEHOLDER,
    )
    const newEntities = getByType(
      state,
      EntityTypeEnum.BlogPostContentPlaceholder,
    )
    return entities.concat(newEntities).length === 1
  }

  return true
}

function checkUniqueSearchResults(state) {
  if (state.type === pageTypes.blogSearch) {
    let popupSearchResultsValidation = true
    const pageSearchResults = getByType(state, EntityTypeEnum.SearchResults)
    //check search results in popups
    pageSearchResults.forEach(entity => {
      const ascendantPopup = entityUtils.getAscendantPopup(state, entity)
      if (ascendantPopup && popupSearchResultsValidation) {
        const visibleSearchResults = entityUtils.getVisibleSearchResults(
          state,
          false,
          ascendantPopup.id,
        )
        popupSearchResultsValidation = visibleSearchResults.length <= 1
      }
    })
    //check search results visible in mobile
    const mobileSearchResultsValidation =
      entityUtils.getVisibleSearchResults(state, false).length <= 1
    //check search results visible in desktop
    const desktopSearchResultsValidation =
      entityUtils.getVisibleSearchResults(state, true).length <= 1

    return (
      mobileSearchResultsValidation &&
      desktopSearchResultsValidation &&
      popupSearchResultsValidation
    )
  }
  return true
}
function checkUniqueBlogPostListing(state) {
  if (
    state.type === pageTypes.blogHome ||
    state.type === pageTypes.blogStatic ||
    state.type === pageTypes.blogSearch ||
    state.type === pageTypes.blogPostListing
  ) {
    let popupBlogPostListingsValidation = true
    const pageBlogPostListings = getByType(
      state,
      EntityTypeEnum.BlogPostListing,
    )
    //check blog post listings in popups
    pageBlogPostListings.forEach(entity => {
      const ascendantPopup = entityUtils.getAscendantPopup(state, entity)
      if (ascendantPopup && popupBlogPostListingsValidation) {
        const visibleListings = entityUtils.getVisibleBlogPostListings(
          state,
          false,
          ascendantPopup.id,
        )
        popupBlogPostListingsValidation = visibleListings.length <= 1
      }
    })
    //check blog post listings visible in mobile
    const mobileBlogPostListingsValidation =
      entityUtils.getVisibleBlogPostListings(state, false).length <= 1
    //check blog post listings visible in desktop
    const desktopBlogPostListingsValidation =
      entityUtils.getVisibleBlogPostListings(state, true).length <= 1

    return (
      mobileBlogPostListingsValidation &&
      desktopBlogPostListingsValidation &&
      popupBlogPostListingsValidation
    )
  }
  return true
}

const checkBlogPostListingEntityExistence = state => {
  if (state.type === PageTypeEnum.BlogPostListing) {
    const blogPostListingEntity = getByType(
      state,
      EntityTypeEnum.BlogPostListing,
    )

    return !!blogPostListingEntity
  }

  return true
}

const checkUniqueCategoryForLatestBlogPosts = state => {
  const latestBlogPostEntities = getByType(
    state,
    EntityTypeEnum.LatestBlogPosts,
  )
  const duplicates = latestBlogPostEntities.filter(
    (item, idx, arr) =>
      idx !==
      arr.findIndex(el => el.postsReplacement === item.postsReplacement),
  )

  return !duplicates.length
}

const checkBlogPageLayoutOnePlaceholder = state => {
  if (state.type === pageTypes.blogLayout) {
    const entities = getByType(state, structureTypes.BLOG_CONTENT_PLACEHOLDER)

    const newEntities = getByType(state, EntityTypeEnum.BlogContentPlaceholder)
    return entities.concat(newEntities).length === 1
  }

  return true
}

const checkOneCustomLogin = state => {
  if (state.type === pageTypes.customLogin) {
    const entities = getByType(state, EntityTypeEnum.CustomLogin)
    return entities.length === 1
  }

  return true
}

// @TODO: I think this logic needs to be refactored
const checkCustomLoginHasNotMultipleFieldsInLoginMode = state => {
  if (state.type === pageTypes.customLogin && checkOneCustomLogin(state)) {
    const customLoginEntity = getByType(state, EntityTypeEnum.CustomLogin)[0]
    const loginStructure = getEntityById(state, customLoginEntity.childIds[0])
    const loginModeEntities = getChildrenEntities(
      state,
      loginStructure.childIds,
    )
    const structureChecker = []
    loginModeEntities.forEach(entity => {
      if (entity.type === structureTypes.FORM_INPUT) {
        if (
          entity.options.slug === slugs.EMAIL ||
          entity.options.slug === slugs.PASSWORD
        ) {
          structureChecker.push(1)
        }
      } else if (entity.type === EntityTypeEnum.Field) {
        if (entity.slug === slugs.EMAIL || entity.slug === slugs.PASSWORD) {
          structureChecker.push(1)
        }
      }

      if (
        (entity.type === structureTypes.BUTTON &&
          entity.options.action === buttonActionsTypes.login) ||
        (entity.type === EntityTypeEnum.Button &&
          entity.action === ButtonActionTypeEnum.Login)
      ) {
        structureChecker.push(1)
      }
    })

    // check if there extra or less required entities
    return structureChecker.join() == [1, 1, 1].join()
  }
  return true
}

// @TODO: I think this logic needs to be refactored
const checkCustomLoginHasNotMultipleFieldsInConfirmRegistrationMode = state => {
  if (state.type === pageTypes.customLogin && checkOneCustomLogin(state)) {
    const customLoginEntity = getByType(state, EntityTypeEnum.CustomLogin)[0]
    const confirmRegistrationStructure = getEntityById(
      state,
      customLoginEntity.childIds[1],
    )
    const confirmRegistrationEntities = getChildrenEntities(
      state,
      confirmRegistrationStructure.childIds,
    )

    const structureChecker = []
    confirmRegistrationEntities.forEach(entity => {
      if (entity.type === structureTypes.FORM_INPUT) {
        if (
          entity.options.slug === slugs.PASSWORD ||
          entity.options.slug === slugs.CONFIRM_PASSWORD
        ) {
          structureChecker.push(1)
        }
      } else if (entity.type === EntityTypeEnum.Field) {
        if (
          entity.slug === slugs.PASSWORD ||
          entity.slug === slugs.CONFIRM_PASSWORD
        ) {
          structureChecker.push(1)
        }
      }

      if (
        (entity.type === structureTypes.BUTTON &&
          entity.options.action === buttonActionsTypes.confirmRegistration) ||
        (entity.type === EntityTypeEnum.Button &&
          entity.action === ButtonActionTypeEnum.ConfirmRegistration)
      ) {
        structureChecker.push(1)
      }
    })

    // check if there extra or less required entities
    return structureChecker.join() == [1, 1, 1].join()
  }

  return true
}

function checkUniqueOptInRecaptcha(state) {
  let isPopupRecaptchaOnlyOne = true
  const pageRecaptchas = getByType(state, EntityTypeEnum.OptInRecaptcha)
  //check recaptchas in popups
  pageRecaptchas.forEach(entity => {
    const ascendantPopup = entityUtils.getAscendantPopup(state, entity)
    if (ascendantPopup && isPopupRecaptchaOnlyOne) {
      const visibleOptInRecaptchas = entityUtils.getVisibleOptInRecaptchas(
        state,
        ascendantPopup.id,
      )
      isPopupRecaptchaOnlyOne = visibleOptInRecaptchas.length <= 1
    }
  })
  //check recaptchas on the page content
  const isOptInRecaptchaOnlyOneMobile =
    entityUtils.getVisibleOptInRecaptchas(state, false).length <= 1
  //check blog post listings visible in desktop
  const isOptInRecaptchaOnlyOneDesktop =
    entityUtils.getVisibleOptInRecaptchas(state, true).length <= 1

  return (
    isOptInRecaptchaOnlyOneMobile &&
    isOptInRecaptchaOnlyOneDesktop &&
    isPopupRecaptchaOnlyOne
  )
}

// const hasEmailSlug = entity => entity.options.slug === slugs.EMAIL

// const checkEveryFormHasEmail = state => getByType(state, FORM)
//   .every(form => findDescendantsByType(state, form.id, FORM_INPUT).some(hasEmailSlug))
//
// const checkEveryInputHasTypeId = state => getByType(state, FORM_INPUT)
//   .every(hasInputTypeId)

// export const validatePage = checker(
//   validator('some forms not contain email input', checkEveryFormHasEmail),
//   validator('some forms contain empty input type.', checkEveryInputHasTypeId)
// )
export const validatePage = checker(
  validator(
    'validation.page.content_length_exceeded',
    checkContentLengthNotExceeded,
  ),
  validator(
    'validation.page.multiple_payment_methods',
    checkHasNotMultiplePaymentMethods,
  ),
  validator(
    'validation.page.paid_calendar.remove_payment_button',
    checkHasPaymentButton,
  ),
  validator(
    'validation.page.payment_button_required',
    checkHasPaymentButtonInOfferPage,
  ),
  validator(
    'validation.page.paid_calendar.one_element',
    checkHasNotMultiplePaidCalendars,
  ),
  validator(
    'validation.page.paid_calendar.paid_calendar_required',
    checkHasPaidCalendarWhenStateHasBooking,
  ),
  validator(
    'validation.page.product_element_required',
    checkHasProductElementWhenStateHasProduct,
  ),
  // validator(
  //   'validation.page.has_unused_or_extra_fields',
  //   checkNoUnUsedExtraFields,
  // ),
  validator(
    'validation.page.blog_post_content.one_placeholder',
    checkBlogPostContentOnePlaceholder,
  ),
  validator(
    'validation.page.blog_post_listing_unique',
    checkUniqueBlogPostListing,
  ),
  validator('validation.page.search_results_unique', checkUniqueSearchResults),
  validator(
    'validation.page.search_results_required',
    checkHasSearchResultsInSearchPage,
  ),
  validator(
    'validation.page.blog_post_list.has_no_listing',
    checkBlogPostListingEntityExistence,
  ),
  validator(
    'validation.page.blog_layout.one_placeholder',
    checkBlogPageLayoutOnePlaceholder,
  ),
  validator(
    'validation.page.custom_login.one_custom_login_element',
    checkOneCustomLogin,
  ),
  validator(
    'validation.page.custom_login.login_form_required_elements',
    checkCustomLoginHasNotMultipleFieldsInLoginMode,
  ),
  validator(
    'validation.page.calendar.one_element',
    checkHasNotMultipleCalendars,
  ),
  validator(
    'validation.page.calendar.event_not_selected',
    checkCalendarEventSelected,
  ),
  validator(
    'validation.page.custom_login.confirm_registration_form_required_elements',
    checkCustomLoginHasNotMultipleFieldsInConfirmRegistrationMode,
  ),
  // validator(
  //   'validation.page.custom_login.resetting_form_required_elements',
  //   checkCustomLoginHasNotMultipleFieldsInRessetingMode,
  // ),
  validator('validation.page.recaptcha_unique', checkUniqueOptInRecaptcha),
  validator(
    'validation.page.latest_blog_posts',
    checkUniqueCategoryForLatestBlogPosts,
  ),
)

export const validateFormInputs = (page, paymentMethods, inputTypes) => {
  if (page.type !== pageTypes.offerPage) {
    return
  }
  const fixedT = i18n.getFixedT(page.locale)

  const deprecatedFields = getByType(page, structureTypes.FORM_INPUT)
  const fields = getByType(page, EntityTypeEnum.Field)

  const fieldSlugs = deprecatedFields
    .map(entity => entity.options.slug)
    .concat(fields.map(entity => entity.slug))

  const isAllRequiredFieldsAdded = {}

  paymentMethods.forEach(paymentMethod => {
    const requiredFields = paymentProcessorsRequiredFields[paymentMethod] || []
    requiredFields.forEach(fieldType => {
      if (
        !fieldSlugs.includes(fieldType) &&
        !isAllRequiredFieldsAdded[fieldType]
      ) {
        isAllRequiredFieldsAdded[fieldType] = inputTypes[fieldType].name
      }
    })
  })

  const requiredFieldNamesArr = Object.values(isAllRequiredFieldsAdded)

  if (requiredFieldNamesArr.length) {
    throw new PageValidationErrorNew(
      fixedT('validation.page.offer_form.form_inputs_required_icu', {
        fieldName: requiredFieldNamesArr.join(', '),
        count: requiredFieldNamesArr.length,
      }),
    )
  }
}
